"use client";
import { createDialogComponent } from "../dialog/dialog.js";
import { useHovercard } from "../hovercard/hovercard.js";
import { TooltipScopedContextProvider, useTooltipProviderContext } from "./tooltip-context.js";
import { createElement, createHook, forwardRef, useWrapElement } from "@ariakit/react-utils";
import { contains, invariant, isFalsyBooleanCallback } from "@ariakit/utils";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/tooltip/tooltip.tsx
const TagName = "div";
/**
* Returns props to create a `Tooltip` component.
* @see https://ariakit.com/components/tooltip
* @example
* ```jsx
* const store = useToolTipStore();
* const props = useTooltip({ store });
* <TooltipAnchor store={store}>Anchor</TooltipAnchor>
* <Role {...props}>Tooltip</Role>
* ```
*/
const useTooltip = createHook(function useTooltip({ store, portal = true, gutter = 8, preserveTabOrder = false, hideOnHoverOutside = true, hideOnInteractOutside = true, ...props }) {
	const context = useTooltipProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "Tooltip must receive a `store` prop or be wrapped in a TooltipProvider component.");
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(TooltipScopedContextProvider, {
		value: store,
		children: element
	}), [store]);
	props = {
		role: useStoreState(store, (state) => state.type === "description" ? "tooltip" : "none"),
		...props
	};
	props = useHovercard({
		...props,
		store,
		portal,
		gutter,
		preserveTabOrder,
		hideOnHoverOutside(event) {
			if (isFalsyBooleanCallback(hideOnHoverOutside, event)) return false;
			const anchorElement = store?.getState().anchorElement;
			if (!anchorElement) return true;
			if ("focusVisible" in anchorElement.dataset) return false;
			return true;
		},
		hideOnInteractOutside: (event) => {
			if (isFalsyBooleanCallback(hideOnInteractOutside, event)) return false;
			const anchorElement = store?.getState().anchorElement;
			if (!anchorElement) return true;
			if (contains(anchorElement, event.target)) return false;
			return true;
		}
	});
	return props;
});
/**
* Renders a tooltip element that visually describes a
* [`TooltipAnchor`](https://ariakit.com/reference/tooltip-anchor) when it
* receives focus or is hovered.
*
* The tooltip is strictly for visual purposes. It's your responsibility to
* ensure the anchor element has an accessible name. See [Tooltip anchors must
* have accessible
* names](https://ariakit.com/components/tooltip#tooltip-anchors-must-have-accessible-names)
* @see https://ariakit.com/components/tooltip
* @example
* ```jsx {3}
* <TooltipProvider>
*   <TooltipAnchor>Anchor</TooltipAnchor>
*   <Tooltip>Tooltip</Tooltip>
* </TooltipProvider>
* ```
*/
const Tooltip = createDialogComponent(forwardRef(function Tooltip(props) {
	return createElement(TagName, useTooltip(props));
}), useTooltipProviderContext);
//#endregion
export { Tooltip, useTooltip };

//# sourceMappingURL=tooltip.js.map